import { useState, useRef, useEffect } from 'preact/hooks';
import { useI18n } from '../i18n/I18nContext';

export function BentoTopNav({ user, workspace, onLogout, currentPath, onRestartTour }) {
  const { locale, setLocale, languages, t } = useI18n();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { path: '/', label: t('nav.dashboard') },
    { path: '/wallchart', label: t('nav.planner') },
    { path: '/requests', label: t('nav.requests') },
    { path: '/calendar', label: t('nav.calendar') },
  ];

  return (
    <>
      <nav class="bento-topnav">
        {/* Mobile: Row 1 - Logo left, Company right */}
        <div class="bento-topnav-mobile-row1">
          <div class="bento-topnav-left">
            <a href="/" class="bento-topnav-logo">
              <img src="/unplug-logo.png" alt="Unplug" class="bento-topnav-logo-img" />
            </a>
            <a href="/" class="bento-topnav-appname">Unplug</a>
          </div>
          <span class="bento-topnav-workspace-mobile">{workspace?.name || 'Workspace'}</span>
        </div>

        {/* Mobile: Row 2 - Menu left, Avatar right */}
        <div class="bento-topnav-mobile-row2">
          <button
            class="bento-mobile-menu-btn"
            onClick={() => setMobileNavOpen(!mobileNavOpen)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 12h18M3 6h18M3 18h18" />
            </svg>
            <span class="mobile-menu-label">Menu</span>
          </button>
          <div class="header-dropdown bento-user-dropdown-mobile" ref={userMenuRef}>
            <button
              class="bento-user-btn"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
            >
              <div class="bento-user-avatar">
                {user?.avatar ? (
                  <img src={user.avatar} alt="" />
                ) : (
                  user?.name?.charAt(0) || 'U'
                )}
              </div>
              <span class="bento-user-name">{user?.name?.split(' ')[0] || 'User'}</span>
            </button>
          </div>
        </div>

        {/* Desktop: Original layout */}
        <div class="bento-topnav-left bento-desktop-only">
          <a href="/" class="bento-topnav-logo">
            <img src="/unplug-logo.png" alt="Unplug" class="bento-topnav-logo-img" />
          </a>
          <a href="/" class="bento-topnav-appname">Unplug</a>
        </div>

        <div class="bento-topnav-center">
          {navLinks.map(link => (
            <a
              key={link.path}
              href={link.path}
              class={`bento-nav-link ${currentPath === link.path ? 'active' : ''}`}
            >
              {link.label}
            </a>
          ))}
        </div>

        <div class="bento-topnav-right bento-desktop-only">
          <span class="bento-topnav-workspace">{workspace?.name || 'Workspace'}</span>

          <div class="header-dropdown" ref={userMenuRef} style={{ position: 'relative' }}>
            <button
              class="bento-user-btn"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
            >
              <div class="bento-user-avatar">
                {user?.avatar ? (
                  <img src={user.avatar} alt="" />
                ) : (
                  user?.name?.charAt(0) || 'U'
                )}
              </div>
              <span class="bento-user-name">{user?.name?.split(' ')[0] || 'User'}</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>

            {userMenuOpen && (
              <div class="bento-user-dropdown">
                {(user?.role === 'owner' || user?.role === 'admin' || user?.role === 'approver') && (
                  <>
                    <a href="/team">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                      </svg>
                      {t('nav.team')}
                    </a>
                    <a href="/settings">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="3" />
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                      </svg>
                      {t('nav.settings')}
                    </a>
                    {/* Billing is owner-only */}
                    {user?.role === 'owner' && (
                      <a href="/billing">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                          <line x1="1" y1="10" x2="23" y2="10" />
                        </svg>
                        {t('nav.billing')}
                      </a>
                    )}
                    {/* Audit Trail - owner and admin only */}
                    {(user?.role === 'owner' || user?.role === 'admin') && (
                      <a href="/audit-trail">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                          <polyline points="10 9 9 9 8 9" />
                        </svg>
                        {t('nav.audit_trail')}
                      </a>
                    )}
                    <div class="dropdown-divider" />
                  </>
                )}
                <a href="/profile?tab=balances">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  </svg>
                  {t('nav.my_balances')}
                </a>
                <a href="/profile?tab=history">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  {t('nav.leave_history')}
                </a>
                <a href="/help">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  {t('nav.help_center')}
                </a>
                {onRestartTour && (
                  <button onClick={() => { setUserMenuOpen(false); onRestartTour(); }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                      <path d="M3 3v5h5" />
                    </svg>
                    {t('nav.restart_tour')}
                  </button>
                )}
                <div class="dropdown-divider" />
                <div class="dropdown-item-label" style={{ padding: '0.5rem 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </svg>
                  <select
                    value={locale}
                    onChange={(e) => setLocale(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '0.375rem 0.5rem',
                      fontSize: '0.875rem',
                      border: '1px solid var(--color-border, #e5e7eb)',
                      borderRadius: '6px',
                      background: 'var(--color-bg, #fff)',
                      cursor: 'pointer'
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {languages.map((lang) => (
                      <option key={lang.code} value={lang.code}>
                        {lang.nativeName}
                      </option>
                    ))}
                  </select>
                </div>
                <div class="dropdown-divider" />
                <button class="danger" onClick={onLogout}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16,17 21,12 16,7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  {t('nav.logout')}
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {mobileNavOpen && (
        <div class="bento-mobile-nav">
          {navLinks.map(link => (
            <a
              key={link.path}
              href={link.path}
              class={`bento-nav-link ${currentPath === link.path ? 'active' : ''}`}
              onClick={() => setMobileNavOpen(false)}
            >
              {link.label}
            </a>
          ))}
          <a href="/team" class="bento-nav-link" onClick={() => setMobileNavOpen(false)}>{t('nav.team')}</a>
          <a href="/settings" class="bento-nav-link" onClick={() => setMobileNavOpen(false)}>{t('nav.settings')}</a>
        </div>
      )}
    </>
  );
}
